/**
 * ACTIVITY-CORE — human-readable feed text (i18n).
 *
 * App-SPECIFIC part (adapt when cloning): the entity_type+action →
 * phrase. The structure (patch, grouping, offline) lives in @stockpilot/shared.
 * Message descriptors `msg` (not strings) are returned so the language is reactive.
 *
 * Stockpilot variant (§1b "adjustment reason"): `stock_move` entries ALWAYS show
 * the signed delta and its REASON — "Ana adjusted Bolt M4: −3 (cycle count)".
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  type ActivityRow,
  fromMinorQty,
  isAdjustmentReason,
  parseActivityPatch,
} from "@stockpilot/shared";

interface ActivitySentence {
  /** Phrase with `{name}` interpolated. */
  named: MessageDescriptor;
  /** Phrase without a name (the patch does not contain one). */
  anonymous: MessageDescriptor;
}

/**
 * Fine semantic key: refines `updated` with what actually happened
 * (confirming an operation, archiving a product…) inspecting the patch.
 * In Stockpilot there is NO purge: `deleted` is ALWAYS trash (`trashed`).
 * Moves are only created: their key is `adjusted` (manual adjustment with
 * canonical reason) or `moved` (operation/receipt/etc movements).
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "stock_move" && action === "created") {
    const reason = typeof patch.reason?.new === "string" ? patch.reason.new : null;
    return isAdjustmentReason(reason) ? "adjusted" : "moved";
  }
  if (action === "updated") {
    if (row.entity_type === "stock_picking" && patch.state) {
      if (patch.state.new === "done") {
        return "confirmed";
      }
      if (patch.state.new === "cancelled") {
        return "cancelled";
      }
    }
    if (row.entity_type === "product" && patch.active) {
      return patch.active.new ? "activated" : "archived";
    }
  }
  if (action === "deleted") {
    return "trashed";
  }
  return action;
}

const PRODUCT_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the product "{name}"`, anonymous: msg`created a product` },
  updated: { named: msg`edited the product "{name}"`, anonymous: msg`edited a product` },
  activated: { named: msg`activated the product "{name}"`, anonymous: msg`activated a product` },
  archived: { named: msg`archived the product "{name}"`, anonymous: msg`archived a product` },
  trashed: {
    named: msg`moved the product "{name}" to the trash`,
    anonymous: msg`moved a product to the trash`,
  },
  restored: { named: msg`restored the product "{name}"`, anonymous: msg`restored a product` },
  reverted: {
    named: msg`undid a change on the product "{name}"`,
    anonymous: msg`undid a change on a product`,
  },
};

const WAREHOUSE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the warehouse "{name}"`, anonymous: msg`created a warehouse` },
  updated: { named: msg`edited the warehouse "{name}"`, anonymous: msg`edited a warehouse` },
  trashed: {
    named: msg`moved the warehouse "{name}" to the trash`,
    anonymous: msg`moved a warehouse to the trash`,
  },
  restored: { named: msg`restored the warehouse "{name}"`, anonymous: msg`restored a warehouse` },
  reverted: {
    named: msg`undid a change on the warehouse "{name}"`,
    anonymous: msg`undid a change on a warehouse`,
  },
};

const LOCATION_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the location "{name}"`, anonymous: msg`created a location` },
  updated: { named: msg`edited the location "{name}"`, anonymous: msg`edited a location` },
  trashed: {
    named: msg`moved the location "{name}" to the trash`,
    anonymous: msg`moved a location to the trash`,
  },
  restored: { named: msg`restored the location "{name}"`, anonymous: msg`restored a location` },
  reverted: {
    named: msg`undid a change on the location "{name}"`,
    anonymous: msg`undid a change on a location`,
  },
};

const PICKING_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the operation "{name}"`, anonymous: msg`created an operation` },
  updated: { named: msg`edited the operation "{name}"`, anonymous: msg`edited an operation` },
  confirmed: {
    named: msg`confirmed the operation "{name}"`,
    anonymous: msg`confirmed an operation`,
  },
  cancelled: {
    named: msg`cancelled the operation "{name}"`,
    anonymous: msg`cancelled an operation`,
  },
  trashed: {
    named: msg`moved the operation "{name}" to the trash`,
    anonymous: msg`moved an operation to the trash`,
  },
  restored: { named: msg`restored the operation "{name}"`, anonymous: msg`restored an operation` },
  reverted: {
    named: msg`undid a change on the operation "{name}"`,
    anonymous: msg`undid a change on an operation`,
  },
};

/** Moves: the name is the PRODUCT and they always carry signed delta + reason. */
const MOVE_TEXTS: Record<string, ActivitySentence> = {
  adjusted: {
    named: msg`adjusted "{name}": {delta} ({reason})`,
    anonymous: msg`adjusted stock: {delta} ({reason})`,
  },
  moved: {
    named: msg`recorded a stock move of "{name}": {delta} ({reason})`,
    anonymous: msg`recorded a stock move: {delta} ({reason})`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** i18n labels for canonical adjustment reasons (enum tokens). */
const REASON_LABELS: Record<string, MessageDescriptor> = {
  merma: msg`shrinkage`,
  rotura: msg`breakage`,
  robo: msg`theft`,
  recuento: msg`recount`,
  otro: msg`other`,
};

export function adjustmentReasonLabel(reason: string): MessageDescriptor | null {
  return REASON_LABELS[reason] ?? null;
}

/** Visible entity name extracted from the patch (name/reference, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["name", "reference", "title"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/** Signed delta of the move in UI units ("+3" / "−3"). */
export function moveDeltaLabel(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  const raw = patch.qty?.new ?? patch.qty?.old;
  const qty = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : null;
  if (qty === null || Number.isNaN(qty)) {
    return null;
  }
  const units = fromMinorQty(qty);
  return units >= 0 ? `+${units}` : `−${Math.abs(units)}`;
}

interface SentenceOptions {
  /** Resolves the product name of a move (id → local name). */
  productName?: (id: string | null) => string | null;
  /** Translator for reason labels (t from i18n provider). */
  translateReason?: (descriptor: MessageDescriptor) => string;
}

/**
 * i18n phrase for an entry: descriptor + values. Render with
 * `t(descriptor, values)` (or i18n._) in the component.
 */
export function activitySentence(
  row: ActivityRow,
  options: SentenceOptions = {},
): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const patch = parseActivityPatch(row.patch);

  if (row.entity_type === "stock_move") {
    const sentence = MOVE_TEXTS[key] ?? FALLBACK;
    const productId = typeof patch.product_id?.new === "string" ? patch.product_id.new : null;
    const name = options.productName?.(productId) ?? null;
    const delta = moveDeltaLabel(row) ?? "";
    const rawReason = typeof patch.reason?.new === "string" ? patch.reason.new : "";
    const label = adjustmentReasonLabel(rawReason);
    const reason = label && options.translateReason ? options.translateReason(label) : rawReason;
    if (name) {
      return { descriptor: sentence.named, values: { name, delta, reason } };
    }
    return { descriptor: sentence.anonymous, values: { delta, reason } };
  }

  const table =
    row.entity_type === "product"
      ? PRODUCT_TEXTS
      : row.entity_type === "warehouse"
        ? WAREHOUSE_TEXTS
        : row.entity_type === "location"
          ? LOCATION_TEXTS
          : row.entity_type === "stock_picking"
            ? PICKING_TEXTS
            : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "5 min ago" localized with Intl; falls back to day/date for older items. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
